import axios from "axios";

const DEFAULT_API_URL = "http://localhost:8000";

const runtimeEnv =
  typeof window !== "undefined" && window.__ENV__
    ? window.__ENV__.REACT_APP_API_URL
    : "";
const configuredUrl = (process.env.REACT_APP_API_URL || runtimeEnv || "").trim();

const API_URL = configuredUrl !== "" ? configuredUrl : DEFAULT_API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Normative Profiles Endpoints
export const getNormativeProfiles = () => apiClient.get("/normative-profiles/");

// Patient Endpoints
export const getPatients = () => apiClient.get("/patients/");

export const createPatient = (patientData) =>
  apiClient.post("/patients/", patientData);

export const getPatientById = (patientId) =>
  apiClient.get(`/patients/${patientId}`);

export const updatePatient = (patientId, patientData) =>
  apiClient.put(`/patients/${patientId}`, patientData);

export const deletePatient = (patientId) =>
  apiClient.delete(`/patients/${patientId}`);

// Session Endpoints
export const getSessions = (patientId) =>
  apiClient.get(`/patients/${patientId}/sessions`); // Assuming sessions are fetched via patient

export const createSession = (sessionData) =>
  apiClient.post("/sessions/", sessionData);

export const getSessionDetails = (sessionId) =>
  apiClient.get(`/sessions/${sessionId}`);

export const updateSession = (sessionId, sessionData) =>
  apiClient.put(`/sessions/${sessionId}`, sessionData);

export const deleteSession = (sessionId) =>
  apiClient.delete(`/sessions/${sessionId}`);

// qEEG Analysis Endpoints
export const uploadEEGDocx = (
  sessionId,
  formData,
  autoAnalyze = true,
  normativeProfileName,
  machineType = "fundacja_orchidea"
) => {
  // formData should be an instance of FormData
  const url = `/sessions/${sessionId}/upload-eeg-docx?auto_analyze=${autoAnalyze}&normative_profile_name=${encodeURIComponent(
    normativeProfileName
  )}&machine_type=${encodeURIComponent(machineType)}`;
  return apiClient.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const analyzeEEGData = (
  sessionId,
  normativeProfileName,
  machineType = "fundacja_orchidea"
) => {
  const params = new URLSearchParams();
  params.append("normative_profile_name", normativeProfileName);
  params.append("machine_type", machineType);
  return apiClient.post(`/sessions/${sessionId}/analyze-eeg`, params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

export const getSessionDeviations = (sessionId) =>
  apiClient.get(`/sessions/${sessionId}/deviations`);

export const updateDeviation = (deviationId, deviationData) =>
  apiClient.put(`/deviations/${deviationId}`, deviationData);

export const getSessionHeatmap = (
  sessionId,
  normativeProfileName,
  machineType = "fundacja_orchidea"
) =>
  apiClient.get(
    `/sessions/${sessionId}/heatmap?normative_profile_name=${encodeURIComponent(
      normativeProfileName
    )}&machine_type=${encodeURIComponent(machineType)}`
  );

export const getSessionRatios = (sessionId) =>
  apiClient.get(`/sessions/${sessionId}/ratios`);

// Diagnostics Center & Interpretation Endpoints
export const getDiagnosticPatterns = (filters) =>
  apiClient.get("/diagnostics-center/patterns", { params: filters });

export const getDiagnosticPattern = (patternId) =>
  apiClient.get(`/diagnostics-center/patterns/${patternId}`);

export const createDiagnosticsPattern = (patternData) =>
  apiClient.post("/diagnostics-center/", patternData);

// Renamed and updated endpoint for fetching interpretation suggestions
export const suggestInterpretationsForDeviation = (deviationId) =>
  apiClient.post(`/interpretations/suggest-for-deviation/${deviationId}`);

export const suggestPatternsForDeviation = (deviationId) =>
  apiClient.post(`/diagnostics-center/suggest-for-deviation/${deviationId}`);

export const updateDiagnosticsCenterPattern = (deviationId) =>
  apiClient.post(`/diagnostics-center/update-pattern/${deviationId}`);

export const associateInterpretationThemesToDiagnosticPattern = (
  patternId,
  themeIds
) =>
  apiClient.post(`/diagnostics-center/patterns/${patternId}/themes`, {
    theme_ids: themeIds,
  });

// This endpoint remains valid for getting themes tied to a specific DC pattern signature
export const getInterpretationThemesFromPatternSignature = (patternSignature) =>
  apiClient.get(`/diagnostics-center/interpretations/${patternSignature}`);

export const getInterpretationThemes = () =>
  apiClient.get("/interpretation-themes/");

export const getInterpretationTheme = (interpretationThemeId) =>
  apiClient.get(`/interpretation-themes/${interpretationThemeId}`);

export const createInterpretationTheme = (themeData) =>
  apiClient.post("/interpretation-themes/", themeData);

// Session Recommendations
export const getSessionInterpretationThemes = (sessionId, minCoverage = null, measureType = 'amplitude') => {
  const params = new URLSearchParams();
  if (minCoverage !== null) {
    params.append('min_coverage', minCoverage);
  }
  if (measureType) {
    params.append('measure_type', measureType);
  }
  const queryString = params.toString();
  return apiClient.get(`/sessions/${sessionId}/interpretation-themes${queryString ? `?${queryString}` : ''}`);
};

// Contact Form Endpoints
export const submitDemoRequest = (formData) =>
  apiClient.post("/contact/demo-request", formData);

export default apiClient;
